import { ArrowLeft, Mail, Trash2, AlertCircle, Shield } from 'lucide-react'

const Contact = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <a
            href="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </a>
          <div className="flex items-center gap-3">
            <Mail className="w-8 h-8 text-primary-600" />
            <h1 className="text-3xl font-bold text-gray-900">Contact Us & Data Requests</h1>
          </div>
          <p className="text-gray-600 mt-2">Get in touch or manage your data</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* General Contact */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">General Inquiries</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border rounded-lg p-6">
              <Mail className="w-8 h-8 text-primary-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Support</h3>
              <p className="text-gray-600 text-sm mb-3">
                Questions about orders, app features, or general help
              </p>
              <a
                href="mailto:support@nearbies.co.uk"
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                support@nearbies.co.uk
              </a>
            </div>

            <div className="border rounded-lg p-6">
              <Shield className="w-8 h-8 text-primary-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy & Legal</h3>
              <p className="text-gray-600 text-sm mb-3">
                Privacy concerns, legal questions, or compliance matters
              </p>
              <a
                href="mailto:legal@nearbies.co.uk"
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                legal@nearbies.co.uk
              </a>
            </div>
          </div>
        </div>

        {/* Account Deletion */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8 mb-8">
          <div className="flex items-start gap-4">
            <Trash2 className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Delete Your Account</h2>

              <div className="bg-white rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Before You Delete:</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>All your personal data will be permanently deleted within 30 days</li>
                  <li>Your order history will be anonymized for merchant records</li>
                  <li>Loyalty points and rewards will be forfeited</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-900 font-semibold mb-1">Alternative: Deactivate Your Account</p>
                    <p className="text-sm text-yellow-800">
                      If you want to take a break but keep your data, you can deactivate your account
                      from the app settings instead of deleting it.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">How to Delete Your Account:</h3>

                <div className="bg-white rounded-lg p-6 border-l-4 border-primary-600">
                  <p className="font-semibold text-gray-900 mb-3">Login to the app to request account deletion</p>
                  <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4 text-sm">
                    <li>Open the Nearbies app or visit <a href="https://app.nearbies.co.uk" className="text-primary-600 underline">app.nearbies.co.uk</a></li>
                    <li>Login with your account credentials</li>
                    <li>Go to <strong>Profile → Settings</strong></li>
                    <li>Scroll to <strong>"Account Management"</strong></li>
                    <li>Tap <strong>"Delete My Account"</strong></li>
                    <li>Confirm deletion to permanently remove your account</li>
                  </ol>
                  <p className="text-gray-600 text-sm mt-4 italic">
                    Your account and all personal data will be permanently deleted within 30 days.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-700">
                    <strong>Need help?</strong> If you're unable to login to request deletion, contact us at{' '}
                    <a
                      href="mailto:privacy@nearbies.co.uk?subject=Account Deletion Help"
                      className="text-primary-600 underline font-semibold"
                    >
                      privacy@nearbies.co.uk
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* GDPR Data Rights */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Data Rights (GDPR)</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Under UK GDPR, you have the following rights regarding your personal data:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">📄 Access Your Data</h3>
              <p className="text-gray-600 text-sm">Request a copy of all data we hold about you</p>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">✏️ Correct Your Data</h3>
              <p className="text-gray-600 text-sm">Update inaccurate or incomplete information</p>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">🗑️ Delete Your Data</h3>
              <p className="text-gray-600 text-sm">Request permanent deletion of your account</p>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">📦 Export Your Data</h3>
              <p className="text-gray-600 text-sm">Download your data in a portable format</p>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">🚫 Restrict Processing</h3>
              <p className="text-gray-600 text-sm">Limit how we use your personal information</p>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">⛔ Object to Processing</h3>
              <p className="text-gray-600 text-sm">Opt out of marketing and certain data uses</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
            <p className="text-sm text-blue-900 mb-3">
              <strong>To exercise any of these rights:</strong>
            </p>
            <p className="text-sm text-blue-800">
              Email us at{' '}
              <a
                href="mailto:privacy@nearbies.co.uk?subject=GDPR Data Rights Request"
                className="text-primary-600 underline font-semibold"
              >
                privacy@nearbies.co.uk
              </a>
              {' '}with "GDPR Data Rights Request" in the subject line. We will respond within 30 days.
            </p>
          </div>
        </div>

        {/* Response Times */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Response Times</h2>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
              <div>
                <p className="font-semibold text-gray-900">General Support</p>
                <p className="text-gray-600 text-sm">We aim to respond within 24-48 hours</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
              <div>
                <p className="font-semibold text-gray-900">Account Deletion Requests</p>
                <p className="text-gray-600 text-sm">Processed within 30 days as required by law</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              <div>
                <p className="font-semibold text-gray-900">GDPR Data Requests</p>
                <p className="text-gray-600 text-sm">Response within 30 days (may be extended to 60 days for complex requests)</p>
              </div>
            </div>
          </div>
        </div>

      </main>
    </div>
  )
}

export default Contact
